import { Campaign, BrandListAssociation } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import { bulkModelToViewModelForCampaign, viewModelToModelForCampaign } from './campaign';
import { viewModelToBulkModelForConversionGoals } from './campaign-conversion';
import {
  viewModelToBulkModelForLocationTargets,
  viewModelToBulkModelForSchedule,
  viewModelToBulkModelForDeviceTargets,
  viewModelToBulkModelForGenderTargets,
  viewModelToBulkModelForAgeTargets,
  viewModelToBulkModelForCompanyNameTargets,
  viewModelToBulkModelForIndustryTargets,
  viewModelToBulkModelForJobFunctionTargets,
} from './campaign-criterion';
import { isBulkWrappedResponse } from '../shared/bulk';
import { ERROR_ENTITY, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '../shared/bulk-upsert-errors';
import { GENERIC_ERROR_MESSAGE_KEY, getErrorsPlaceholders } from '../shared/constants';
import { viewModelToBulkModelForDisclaimer } from './ad-extension';
import { viewModelToBulkModelForDsaSettings } from './dsa-settings';

const campaignEDM = edmCampaign.types.resolve('Model.Campaign');
const campaignResource = new campaignEDM.ResourceIdentifier();

export const createOrUpdateCampaign = (data) => {
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

const bulkUpsertCampaignSettingsEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  'Campaign Conversion Goal Error': ERROR_ENTITY.CampaignConversionGoal,
  'Campaign Day Time Criterion Error': ERROR_ENTITY.CampaignDayTimeCriterion,
  'Campaign DeviceOS Criterion Error': ERROR_ENTITY.CampaignDeviceOSCriterion,
  'Campaign Gender Criterion Error': ERROR_ENTITY.CampaignGenderCriterion,
  [getBulkUploadErrorType(BrandListAssociation)]: ERROR_ENTITY.BrandListAssociation,
  'Campaign Location Criterion Error': ERROR_ENTITY.CampaignLocation,
  'Campaign Radius Criterion Error': ERROR_ENTITY.CampaignRadius,
  'Campaign Negative Location Criterion Error': ERROR_ENTITY.CampaignNegativeLocation,
  'Campaign Location Intent Criterion Error': ERROR_ENTITY.CampaignLocationIntent,
  'Campaign Company Name Criterion Error': ERROR_ENTITY.CampaignCompanyNameCriterion,
  'Campaign Industry Criterion Error': ERROR_ENTITY.CampaignIndustryCriterion,
  'Campaign Job Function Criterion Error': ERROR_ENTITY.CampaignJobFunctionCriterion,
};

export const saveCampaign = ({
  campaign: campaignViewModel,
  bundleCountries,
  bundleLocationCriterions,
  isEdit,
  permissions,
}, {
  scenario,
  i18n,
  activityLogger,
}) => {
  const campaignModel = viewModelToModelForCampaign(campaignViewModel, isEdit, i18n, permissions);
  const locationTargetsModel = bundleLocationCriterions ? viewModelToBulkModelForLocationTargets(campaignViewModel, bundleLocationCriterions) : [];
  const conversionGoalsAssociations = campaignViewModel && campaignViewModel.conversionGoals ? {
    CampaignConversionGoalAssociations: viewModelToBulkModelForConversionGoals(campaignViewModel, campaignViewModel.Id),
  } : {};
  const adExtensionAssociations = viewModelToBulkModelForDisclaimer(campaignViewModel);
  const scheduleModel = campaignViewModel.adSchedulesBulkUpsert ? viewModelToBulkModelForSchedule(campaignViewModel.adSchedulesBulkUpsert, campaignModel) : [];
  const deviceModel = campaignViewModel.Device ? viewModelToBulkModelForDeviceTargets({ campaignViewModel }) : [];
  const dsaSettings = campaignViewModel.hasDsaSettings ? viewModelToBulkModelForDsaSettings({ campaignViewModel, permissions }) : {};
  const genderModel = campaignViewModel.Gender ? viewModelToBulkModelForGenderTargets({ campaignViewModel }) : [];
  const ageModel = campaignViewModel.Age ? viewModelToBulkModelForAgeTargets({ campaignViewModel }) : [];
  const companyNameModel = campaignViewModel.companyNameTargets ? viewModelToBulkModelForCompanyNameTargets({ campaignViewModel }) : [];
  const industryModel = campaignViewModel.industry ? viewModelToBulkModelForIndustryTargets({ campaignViewModel }) : [];
  const jobFunctionModel = campaignViewModel.jobTargets ? viewModelToBulkModelForJobFunctionTargets({ campaignViewModel }) : [];
  const modelToSave = {
    ...campaignModel,
    ...conversionGoalsAssociations,
    ...adExtensionAssociations,
    ...dsaSettings,
    CampaignCriterions: [
      ...locationTargetsModel,
      ...scheduleModel,
      ...deviceModel,
      ...genderModel,
      ...ageModel,
      ...companyNameModel,
      ...industryModel,
      ...jobFunctionModel,
    ],
  };

  return createOrUpdateCampaign(modelToSave)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const data = {
        campaign: bulkModelToViewModelForCampaign({
          campaignModel: result,
          originalCampaignModel: campaignViewModel,
          bundleCountries,
          permissions,
        }),
        ...getErrorsPlaceholders(),
      };
      return data;
    }, (bulkOrSystemError) => {
      if (isBulkWrappedResponse(bulkOrSystemError)) {
        const { entity, errors } = bulkOrSystemError;
        const campaign = entity ?
          bulkModelToViewModelForCampaign({
            campaignModel: entity,
            originalCampaignModel: campaignViewModel,
            bundleCountries,
            errors,
            isEdit,
            permissions,
          }) :
          campaignViewModel;

        let filteredErrors = errors;
        if (campaign.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }

        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertCampaignSettingsEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          campaign,
          serverErrors,
        });
      }

      // Activity
      // eslint-disable-next-line no-console
      console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        campaign: campaignViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      });
    });
};
